// src/components/ConfirmationModal.tsx
import React, { useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { XIcon, TrashIcon } from './icons';
import { getRoddoTheme, resolveRoddoDarkMode } from '../constants/theme';
import { useRoutePortalTarget } from './layout/RoutePortalHost';

type LegacyProps = {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
};

type NewProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isDarkMode?: boolean;
};

type Props = LegacyProps | NewProps;

const isLegacyProps = (p: Props): p is LegacyProps =>
  (p as any).description !== undefined && typeof (p as any).onCancel === 'function';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const ConfirmationModal: React.FC<Props> = (props) => {
  const portalTarget = useRoutePortalTarget();
  const legacy = isLegacyProps(props);

  const modalRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  const isOpen = legacy ? true : props.isOpen;
  const title = props.title;
  const message = legacy ? props.description : props.message;
  const isDarkMode = resolveRoddoDarkMode(legacy ? undefined : props.isDarkMode);

  const confirmText = legacy ? props.confirmLabel ?? 'Delete' : props.confirmText ?? 'Delete';
  const cancelText = legacy ? props.cancelLabel ?? 'Cancel' : props.cancelText ?? 'Cancel';
  const onClose = legacy ? props.onCancel : props.onClose;
  const onConfirm = props.onConfirm;
  const theme = getRoddoTheme(isDarkMode);

  const getFocusable = useMemo(() => {
    return (): HTMLElement[] => {
      const root = modalRef.current;
      if (!root) return [];
      return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)) as HTMLElement[];
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    previouslyFocusedElement.current = document.activeElement as HTMLElement;
    const focusable = getFocusable();
    if (focusable.length > 0) {
      const cancelCandidate = focusable[1] ?? focusable[0];
      cancelCandidate?.focus();
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const nodes = getFocusable();
      if (nodes.length === 0) return;

      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey) {
        if (document.activeElement === first) {
          last.focus();
          event.preventDefault();
        }
      } else if (document.activeElement === last) {
        first.focus();
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedElement.current?.focus?.();
    };
  }, [getFocusable, isOpen, onClose]);

  if (!isOpen) return null;

  const modal = (
    <div
      className="fixed left-0 right-0 z-[50000] flex min-h-0 items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmation-dialog-title"
      style={{
        top: 'calc(var(--roddo-header-h, 96px) + 8px)',
        bottom: 'calc(var(--roddo-nav-h, 84px) + 8px)',
        background: isDarkMode ? 'rgba(9,13,24,0.72)' : 'rgba(32, 24, 14, 0.24)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
      }}
    >
      <div
        ref={modalRef}
        className="relative max-h-full w-full max-w-sm overflow-y-auto overscroll-contain rounded-[18px] border p-6"
        onClick={(e) => e.stopPropagation()}
        role="document"
        data-roddo-theme={isDarkMode ? 'dark' : 'light'}
        style={{
          background: isDarkMode ? '#101827' : '#F7EFE6',
          borderColor: theme.borderStrong,
          boxShadow: isDarkMode
            ? '0 8px 22px rgba(0,0,0,0.24)'
            : '0 10px 26px rgba(89,65,37,0.10)',
          color: theme.text,
        }}
      >

        <div className="relative z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div
                className="flex h-12 w-12 items-center justify-center rounded-[12px] border"
                style={{
                  background: isDarkMode ? 'rgba(239,106,115,0.10)' : '#FDECEC',
                  borderColor: isDarkMode ? 'rgba(248,113,113,0.26)' : 'rgba(239,68,68,0.16)',
                  boxShadow: isDarkMode
                    ? '0 6px 16px rgba(127,29,29,0.16)'
                    : '0 5px 14px rgba(239,68,68,0.08)',
                }}
              >
                <TrashIcon className="h-6 w-6 text-red-500" aria-hidden="true" />
              </div>

              <div className="pt-0.5">
                <h3
                  id="confirmation-dialog-title"
                  className="text-lg font-extrabold tracking-tight"
                  style={{ color: theme.text }}
                >
                  {title}
                </h3>
                <p className="mt-2 text-sm leading-6" style={{ color: theme.textSoft }}>
                  {message}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="grid h-9 w-9 place-items-center rounded-[12px] border transition-colors hover:bg-black/5 dark:hover:bg-white/5"
              aria-label="Close"
              style={{
                borderColor: theme.border,
                background: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.55)',
              }}
            >
              <XIcon className="h-4 w-4 opacity-80" />
            </button>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="inline-flex w-full items-center justify-center rounded-[12px] border px-5 py-3 text-sm font-bold transition-transform active:translate-y-[1px] sm:w-auto"
              style={{
                borderColor: theme.borderStrong,
                background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.78)',
                color: theme.text,
                boxShadow: isDarkMode
                  ? '0 10px 22px rgba(0,0,0,0.18)'
                  : '0 8px 18px rgba(89,65,37,0.10)',
              }}
              onClick={onClose}
            >
              {cancelText}
            </button>

            <button
              type="button"
              className="inline-flex w-full items-center justify-center rounded-[12px] border px-5 py-3 text-sm font-extrabold text-white transition-transform active:translate-y-[1px] sm:w-auto"
              style={{
                borderColor: 'rgba(255,255,255,0.22)',
                background: '#EF6A73',
                boxShadow: '0 5px 14px rgba(227,63,95,0.20)',
              }}
              onClick={() => {
                onConfirm();
                onClose();
              }}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return portalTarget ? ReactDOM.createPortal(modal, portalTarget) : null;
};

export default ConfirmationModal;
